import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { usePwaInstall } from '../context/PwaInstallContext';
import { hasSeenSurvey, markSurveyShown, surveyCopy, surveyStorageKey, SURVEY_URL } from './surveyCampaign';
import './survey-invite.css';

export function SurveyInvite() {
  const { user } = useAuth();
  const install = usePwaInstall();
  return <AccountSurveyInvite userId={user?.id} blocked={install.open} />;
}

export function AccountSurveyInvite({ userId, blocked = false }: { userId?: string | number; blocked?: boolean }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [visibleKey, setVisibleKey] = useState<string | null>(null);
  const key = userId == null ? null : surveyStorageKey(userId);

  useEffect(() => {
    if (!key || blocked) return;
    let cancelled = false;
    const show = async () => {
      if (document.visibilityState !== 'visible' || document.querySelector('dialog[open], [aria-modal="true"]')) return;
      // Access localStorage inside the guarded helpers: some browsers deny storage entirely.
      const storage = {
        getItem: (name: string) => window.localStorage.getItem(name),
        setItem: (name: string, value: string) => window.localStorage.setItem(name, value),
      };
      if (await hasSeenSurvey(storage, key)) { window.clearInterval(timer); return; }
      if (cancelled) return;
      if (!dialog.current || dialog.current.open) return;
      dialog.current.showModal();
      setVisibleKey(key);
      void markSurveyShown(storage, key);
      window.clearInterval(timer);
    };
    // The first-visit iOS install guide opens after 4.5s; let it take priority.
    // Retry after other dialogs close without recording an unseen invitation.
    const timer = window.setInterval(() => void show(), 6000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [key, blocked]);

  useEffect(() => {
    if (visibleKey && (key !== visibleKey || blocked)) dialog.current?.close();
  }, [key, visibleKey, blocked]);

  return (
    <dialog
      ref={dialog}
      className="survey-invite"
      dir="rtl"
      aria-labelledby="survey-invite-title"
      aria-describedby="survey-invite-description"
      onClick={event => { if (event.target === event.currentTarget) dialog.current?.close(); }}
    >
      <div className="survey-invite__content">
        <img className="survey-invite__image" src="/survey-pebpq8.png" alt="" />
        <h2 id="survey-invite-title">{surveyCopy.title}</h2>
        <p id="survey-invite-description">{surveyCopy.body}</p>
        <p className="survey-invite__privacy">{surveyCopy.privacy}</p>
        <p className="survey-invite__hint">{surveyCopy.vpn}</p>
        <a className="survey-invite__primary" href={SURVEY_URL} target="_blank" rel="noopener noreferrer" onClick={() => dialog.current?.close()}>
          {surveyCopy.open}
        </a>
        <button className="survey-invite__dismiss" type="button" onClick={() => dialog.current?.close()}>{surveyCopy.dismiss}</button>
      </div>
    </dialog>
  );
}
